import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core'

// A fixed-length number entered one digit per box (e.g. a 9-digit account number).
// Digits are typed left to right: focus jumps to the next box, Backspace goes back and pasting fills all boxes.
@Component({
  selector: 'form-row-digits',
  templateUrl: 'form-row-digits.component.html',
  styleUrls: ['form-row-digits.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormRowDigitsComponent implements OnChanges, AfterViewInit {
  @ViewChildren('digitInput') digitInputs: QueryList<ElementRef<HTMLInputElement>>
  @Input() label: string
  @Input() tooltipLabel: string
  @Input() tooltipIcon: string
  @Input() required: boolean
  @Input() requiredCssClass: any
  @Input() errorLabel: string
  @Input() length = 9
  @Input() model: string
  @Input() dataCyAttribute: string
  @Output() modelChange: EventEmitter<string> = new EventEmitter()

  boxes: number[] = []
  // invalid state waits until the user leaves the boxes, so it doesn't flash red from the first digit
  hasFocus = false

  // only the box being typed into is a Tab stop, so Tab leaves the field instead of bouncing between boxes
  get activeIndex(): number {
    return Math.min(this.value.length, this.length - 1)
  }

  get showInvalid(): boolean {
    return !!this.requiredCssClass && !this.hasFocus
  }

  get value(): string {
    return `${this.model ?? ''}`
  }

  ngOnChanges(): void {
    this.boxes = Array.from({ length: this.length }, (_, i) => i)
    this.render()
  }

  ngAfterViewInit(): void {
    this.render()
  }

  onFocusOut(event: FocusEvent): void {
    this.hasFocus = (event.currentTarget as HTMLElement).contains(event.relatedTarget as Node)
  }

  onFocus(index: number): void {
    // no gaps: an empty box after the first empty one sends focus back to the first empty one
    const target = Math.min(index, this.activeIndex)
    if (target !== index) {
      this.focusBox(target)
      return
    }
    this.boxAt(index)?.select()
  }

  onInput(index: number, event: Event): void {
    const typed = (event.target as HTMLInputElement).value
    const digits = typed.replace(/\D/g, '')
    if (!typed) {
      this.setValue(this.value.slice(0, index) + this.value.slice(index + 1), index)
    } else if (digits) {
      // a pasted or autofilled number spreads over the following boxes
      const value = (this.value.slice(0, index) + digits + this.value.slice(index + 1)).slice(0, this.length)
      this.setValue(value, Math.min(index + digits.length, this.length - 1))
    } else {
      this.render()
    }
  }

  onKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (this.value[index] !== undefined) {
        this.setValue(this.value.slice(0, index) + this.value.slice(index + 1), index)
      } else if (index > 0) {
        this.setValue(this.value.slice(0, index - 1) + this.value.slice(index), index - 1)
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      this.focusBox(index - 1)
    } else if (event.key === 'ArrowRight' && index < this.length - 1) {
      event.preventDefault()
      this.focusBox(index + 1)
    }
  }

  private setValue(value: string, focusIndex: number): void {
    this.model = value
    this.render()
    this.modelChange.emit(value)
    this.focusBox(focusIndex)
  }

  // box values are written directly, so a rejected keystroke (a letter) is wiped even when the value didn't change
  private render(): void {
    this.digitInputs?.forEach((input, i) => (input.nativeElement.value = this.value[i] ?? ''))
  }

  private focusBox(index: number): void {
    const box = this.boxAt(index)
    if (box && document.activeElement !== box) {
      box.focus()
    } else {
      box?.select()
    }
  }

  private boxAt(index: number): HTMLInputElement | undefined {
    return this.digitInputs?.get(index)?.nativeElement
  }
}
